import { useEffect } from "react";
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from "motion/react";
import { EASE_OUT, MaskWords } from "../../../kit.jsx";
import { Btn, Kicker } from "../shared.jsx";
import { GROUPS } from "./Isotype.jsx";

const TERMS = [
  { ...GROUPS[0], note: "young innovators, 2 days" },
  { ...GROUPS[1], note: "participants, 27+ countries" },
  { ...GROUPS[2], note: "students, GRID as outreach partner" },
];
const TOTAL = TERMS.reduce((sum, term) => sum + term.people, 0);
const fmt = (value) => Math.round(value).toLocaleString("en-US");

function Rolling({ value, delay }) {
  const reduceMotion = useReducedMotion();
  const mv = useMotionValue(reduceMotion ? value : 0);
  const text = useTransform(mv, (v) => fmt(v));
  useEffect(() => {
    if (reduceMotion) return undefined;
    const controls = animate(mv, value, { duration: 1.4, delay, ease: EASE_OUT });
    return () => controls.stop();
  }, [delay, mv, reduceMotion, value]);
  return (
    <>
      <span className="sr-only">{fmt(value)}</span>
      <motion.span aria-hidden="true">{text}</motion.span>
    </>
  );
}

const JUMPS = [
  ["People", "#people"],
  ["Map", "#map"],
  ["AAROH", "#aaroh"],
  ["Aurora", "#aurora"],
  ["Metamorphosis", "#metamorphosis"],
  ["Timeline", "#timeline"],
];

export default function Opening() {
  const reduceMotion = useReducedMotion();
  const appear = (delay) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: EASE_OUT },
  });
  return (
    <section className="ab-open" aria-labelledby="ab-open-title">
      <motion.div {...appear(0)}><Kicker>Our impact</Kicker></motion.div>
      <MaskWords as="h1" id="ab-open-title" className="ab-display ab-open__title" text="What 2,013 participants look like." onMount delay={0.08} />
      <ol className="ab-sum" aria-label={`${fmt(TOTAL)} participants: ${TERMS.map((t) => `${t.people} at ${t.name}`).join(", ")}`}>
        {TERMS.map((term, i) => (
          <motion.li key={term.key} className={`ab-sum__term is-${term.key}`} {...appear(0.45 + i * 0.12)}>
            <strong><Rolling value={term.people} delay={0.5 + i * 0.12} /></strong>
            <span>{term.name}</span>
            <small>{term.note}</small>
            {i < TERMS.length - 1 && <em className="ab-sum__op" aria-hidden="true">+</em>}
          </motion.li>
        ))}
        <motion.li className="ab-sum__total" {...appear(0.9)}>
          <em className="ab-sum__op" aria-hidden="true">=</em>
          <strong><Rolling value={TOTAL} delay={1} /></strong>
          <span>participants</span>
          <small>across AAROH, Aurora and Metamorphosis</small>
        </motion.li>
      </ol>
      <motion.div className="ab-open__row" {...appear(1.1)}>
        <p className="ab-lede">
          {fmt(TOTAL)} participants across AAROH, Aurora and Metamorphosis. GRID organised AAROH and runs Aurora. Metamorphosis was organised by Tale of Humankind Foundation and IIT Delhi, with GRID as the outreach partner.
        </p>
        <div className="gh-actions">
          <Btn href="#people">See it counted</Btn>
          <Btn to="/programmes" variant="ghost">Our programmes</Btn>
        </div>
      </motion.div>
      <motion.nav className="ab-open__jumps" aria-label="On this page" {...appear(1.2)}>
        {JUMPS.map(([label, href]) => <a key={href} href={href}>{label}</a>)}
      </motion.nav>
    </section>
  );
}
